import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApiClient, PageDto } from "../api/client";
import { Campaign, campaignApi } from "../api/campaigns";
import { PageHeader, PageShell, StatusBadge } from "../components/ui";
import {
  CampaignPager,
  campaignDate,
  campaignName,
  campaignPath,
  campaignRate,
  campaignRates,
} from "../components/CampaignUi";

export const NotificationsPage: React.FC = () => {
  const { request } = useApiClient();
  const api = useMemo(() => campaignApi(request), [request]);
  const [params, setParams] = useSearchParams();
  const page = Math.max(0, Math.floor(Number(params.get("page")) || 0));
  const [campaigns, setCampaigns] = useState<PageDto<Campaign> | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [page]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .list(page)
      .then((result) => {
        if (!cancelled) setCampaigns(result);
      })
      .catch((e) => {
        if (!cancelled)
          setError(
            (e as Error).message || "Kampagnen konnten nicht geladen werden."
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, page, revision]);

  return (
    <PageShell>
      <PageHeader
        title="Kampagnen"
        description="Deine Nachrichten und ihre Wirkung. Alle Kampagnen auf einen Blick."
        actions={
          <>
            <button
              className="btn btn-neutral"
              disabled={loading}
              onClick={() => setRevision((r) => r + 1)}
            >
              Aktualisieren
            </button>
            <Link
              className="btn border-primary bg-primary text-gray-900 hover:bg-amber-400"
              to="/notifications/new"
            >
              ＋ Neue Kampagne
            </Link>
          </>
        }
      />
      <section
        className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
        aria-label="Kampagnenübersicht"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Alle Kampagnen{" "}
              <span className="ml-2 rounded-full bg-gray-100 px-2.5 py-1 text-sm text-gray-500">
                {campaigns?.totalElements ?? "—"}
              </span>
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Entwürfe weiterbearbeiten oder Versand und Ergebnisse ansehen.
            </p>
          </div>
          <span className="text-xs text-gray-500">Neueste zuerst</span>
        </div>
        {error ? (
          <p role="alert" className="m-6 rounded-xl bg-red-50 p-4 text-red-700">
            {error}
          </p>
        ) : loading ? (
          <p role="status" className="p-10 text-center text-gray-500">
            Kampagnen werden geladen …
          </p>
        ) : !campaigns?.content.length ? (
          <div className="px-6 py-16 text-center">
            <div
              className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/20 text-2xl"
              aria-hidden="true"
            >
              ✉
            </div>
            <h3 className="font-semibold text-gray-900">
              Noch keine Kampagnen.
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Starte mit einer E-Mail oder Push-Nachricht an deine Community.
            </p>
            <Link className="btn btn-neutral mt-5" to="/notifications/new">
              Erste Kampagne erstellen
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {campaigns.content.map((c) => (
              <article
                key={c.id}
                className="grid items-center gap-5 px-6 py-6 transition hover:bg-gray-50/70 xl:grid-cols-[minmax(220px,1fr)_minmax(300px,1.2fr)_auto]"
                aria-label={campaignName(c)}
              >
                <div className="min-w-0">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <StatusBadge tone={c.draft ? "amber" : "green"}>
                      {c.draft
                        ? "Entwurf"
                        : c.metrics.queued
                        ? "Versand läuft"
                        : "Versand gestartet"}
                    </StatusBadge>
                    <span className="text-xs font-medium text-gray-500">
                      {c.channel === "EMAIL" ? "E-Mail" : "Push"}
                    </span>
                  </div>
                  <Link
                    to={campaignPath(c)}
                    className="break-words text-base font-semibold text-gray-900 hover:text-secondary hover:underline"
                  >
                    {campaignName(c)}
                  </Link>
                  <p className="mt-1 text-xs text-gray-500">
                    {campaignDate(c.sentAt || c.createdAt)}
                    {!c.draft &&
                      ` · ${c.metrics.targeted} Nutzer in der Zielgruppe`}
                  </p>
                </div>
                <dl className="grid grid-cols-3 gap-3">
                  {campaignRates(c).map((kpi) => (
                    <div key={kpi.label}>
                      <dt className="text-xs text-gray-500">{kpi.label}</dt>
                      <dd
                        className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${
                          c.draft || kpi.value == null
                            ? "text-gray-300"
                            : "text-gray-900"
                        }`}
                        title={
                          c.draft
                            ? "Noch nicht versendet"
                            : c.channel === "PUSH" &&
                              kpi.label !== "Öffnungsrate"
                            ? "Für Push nicht erfasst"
                            : kpi.detail
                        }
                      >
                        {campaignRate(kpi.value)}
                      </dd>
                    </div>
                  ))}
                </dl>
                <Link
                  className="justify-self-start text-sm font-semibold text-secondary hover:underline"
                  to={campaignPath(c)}
                >
                  {c.draft ? "Entwurf bearbeiten" : "Ergebnisse ansehen"}{" "}
                  <span aria-hidden="true">→</span>
                  <span className="sr-only">: {campaignName(c)}</span>
                </Link>
              </article>
            ))}
          </div>
        )}
        {campaigns && campaigns.totalPages > 1 && (
          <div className="border-t border-gray-100 px-6 py-4">
            <CampaignPager
              page={page}
              last={campaigns.last}
              disabled={loading}
              onPage={(p) => setParams(p ? { page: String(p) } : {})}
            />
          </div>
        )}
      </section>
      <p className="mt-4 text-xs leading-relaxed text-gray-500">
        Öffnungs- und Klickrate beziehen sich auf zugestellte E-Mails, die
        Bounce-Rate auf angenommene E-Mails. Bei Push zählt die Öffnungsrate
        Tap-Öffnungen je FCM-Annahme. Ohne Messgrundlage erscheint „—“.
      </p>
    </PageShell>
  );
};
